import { useCallback, useEffect, useMemo, useState } from "react";
import { Edit3, Plus, Shield, ShieldOff, Trash2, KeyRound, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { USERS } from "@/constants/testIds/crm";
import HighlightText from "@/components/data-management/HighlightText";
import SavedViewsBar from "@/components/data-management/SavedViewsBar";
import StatusCountCards from "@/components/data-management/StatusCountCards";
import PaginationBar from "@/components/data-management/PaginationBar";
import { normalizeListResponse } from "@/lib/listResponse";
import { getStatusLabel, getStatusToneClasses } from "@/lib/statusStyles";

const inputCls = "w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors";

const MODULES = [
    { key: "dashboard", label: "Dashboard" },
    { key: "lead_discovery", label: "Lead Discovery" },
    { key: "leads", label: "Leads" },
    { key: "contacts", label: "Contacts" },
    { key: "pipeline", label: "Sales Pipeline" },
    { key: "clients", label: "Clients" },
    { key: "projects", label: "Projects" },
    { key: "invoices", label: "Invoices" },
    { key: "expenses", label: "Expenses" },
    { key: "project_pricing", label: "Project Pricing" },
    { key: "reports", label: "Reports" },
    { key: "users", label: "Users" },
    { key: "settings", label: "Settings" },
];

const ACTIONS = ["view", "create", "edit", "delete", "export"];
const SORT_OPTIONS = [
    { value: "-created_at", label: "Newest first" },
    { value: "created_at", label: "Oldest first" },
    { value: "name", label: "Name A-Z" },
    { value: "email", label: "Email A-Z" },
    { value: "role", label: "Role A-Z" },
];

const emptyPermissions = () => Object.fromEntries(
    MODULES.map((m) => [m.key, { view: true, create: false, edit: false, delete: false, export: false }])
);

const emptyForm = () => ({
    name: "",
    email: "",
    password: "",
    role: "sales",
    active: true,
    custom_role_name: "",
    permissions: emptyPermissions(),
});

export default function Users() {
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({});
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [q, setQ] = useState("");
    const [role, setRole] = useState("");
    const [active, setActive] = useState("");
    const [createdFrom, setCreatedFrom] = useState("");
    const [createdTo, setCreatedTo] = useState("");
    const [sort, setSort] = useState("-created_at");
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [resetFor, setResetFor] = useState(null);
    const [form, setForm] = useState(emptyForm());

    const load = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            params.set("page", page);
            params.set("limit", 10);
            if (q) params.set("q", q);
            if (role) params.set("role", role);
            if (active !== "") params.set("active", active);
            if (createdFrom) params.set("created_from", createdFrom);
            if (createdTo) params.set("created_to", createdTo);
            if (sort) params.set("sort", sort);
            const { data } = await api.get(`/users?${params.toString()}`);
            const normalized = normalizeListResponse(data);
            setItems(normalized.items);
            setTotal(normalized.total);
            setCounts(normalized.counts);
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message || "Failed to load users");
            setItems([]);
            setCounts({});
            setTotal(0);
        }
    }, [active, createdFrom, createdTo, page, q, role, sort]);

    useEffect(() => { load(); }, [load]);

    const openCreate = () => {
        setEditing(null);
        setForm(emptyForm());
        setFormOpen(true);
    };
    const openEdit = (u) => {
        setEditing(u);
        setForm({
            name: u.name || "",
            email: u.email || "",
            password: "",
            role: u.role || "sales",
            active: u.active !== false,
            custom_role_name: u.custom_role_name || "",
            permissions: { ...emptyPermissions(), ...(u.permissions || {}) },
        });
        setFormOpen(true);
    };

    const save = async (e) => {
        e.preventDefault();
        try {
            if (editing?.id) {
                const payload = {
                    name: form.name,
                    email: form.email,
                    role: form.role,
                    active: form.active,
                    custom_role_name: form.custom_role_name,
                    permissions: form.permissions,
                };
                await api.patch(`/users/${editing.id}`, payload);
            } else {
                await api.post("/users", {
                    name: form.name,
                    email: form.email,
                    password: form.password,
                    role: form.role,
                    active: form.active,
                    custom_role_name: form.custom_role_name,
                    permissions: form.permissions,
                });
            }
            toast.success(editing?.id ? "User updated" : "User created");
            setFormOpen(false);
            setEditing(null);
            setForm(emptyForm());
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message || "Save failed");
        }
    };

    const resetPassword = async (u) => {
        const next = window.prompt(`Reset password for ${u.name}`, "");
        if (!next) return;
        try {
            await api.post(`/users/${u.id}/reset-password`, { password: next });
            toast.success("Password reset");
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message || "Reset failed");
        }
    };

    const remove = async (u) => {
        if (!window.confirm(`Delete user "${u.name}"?`)) return;
        try {
            await api.delete(`/users/${u.id}`);
            toast.success("User deleted");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message || "Delete failed");
        }
    };

    const roleSummary = useMemo(() => (role) => {
        if (role === "custom") return "Custom";
        return role?.replaceAll("_", " ");
    }, []);
    const viewState = { q, role, active, createdFrom, createdTo, sort };
    const applyState = (next) => {
        setQ(next.q || "");
        setRole(next.role || "");
        setActive(next.active || "");
        setCreatedFrom(next.createdFrom || "");
        setCreatedTo(next.createdTo || "");
        setSort(next.sort || "-created_at");
        setPage(1);
    };
    const totalPages = Math.max(1, Math.ceil(total / 10));

    return (
        <div className="space-y-6" data-testid={USERS.root}>
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">Administration</div>
                    <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Users</h1>
                    <p className="text-sm text-muted-foreground mt-2">Manage roles, permissions, activation, and password resets.</p>
                </div>
                <button onClick={openCreate} className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90">
                    <Plus className="h-4 w-4" /> New User
                </button>
            </div>

            <SavedViewsBar
                storageKey="crm.users.views"
                state={viewState}
                applyState={applyState}
                defaultState={{ q: "", role: "", active: "", createdFrom: "", createdTo: "", sort: "-created_at" }}
            />

            <StatusCountCards
                cards={[
                    { label: "Total Users", value: total, hint: "All matching users" },
                    { label: "Active", value: counts.true || 0, hint: "Enabled accounts" },
                    { label: "Inactive", value: counts.false || 0, hint: "Disabled accounts" },
                    { label: "Admin access", value: items.filter((u) => ["super_admin", "admin"].includes(u.role)).length, hint: "Current page only" },
                ]}
            />

            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                <input value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} placeholder="Search name, email or custom role…" className="md:col-span-2 h-10 px-3 rounded-md border border-input bg-card text-sm" />
                <select value={role} onChange={(e) => { setPage(1); setRole(e.target.value); }} className="h-10 px-3 rounded-md border border-input bg-card text-sm">
                    <option value="">All roles</option>
                    {["super_admin", "admin", "sales", "project_manager", "developer", "accounts", "custom"].map((r) => <option key={r} value={r}>{r.replaceAll("_", " ")}</option>)}
                </select>
                <select value={active} onChange={(e) => { setPage(1); setActive(e.target.value); }} className="h-10 px-3 rounded-md border border-input bg-card text-sm">
                    <option value="">All status</option>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                </select>
                <input type="date" value={createdFrom} onChange={(e) => { setPage(1); setCreatedFrom(e.target.value); }} className="h-10 px-3 rounded-md border border-input bg-card text-sm" />
                <input type="date" value={createdTo} onChange={(e) => { setPage(1); setCreatedTo(e.target.value); }} className="h-10 px-3 rounded-md border border-input bg-card text-sm" />
                <select value={sort} onChange={(e) => { setPage(1); setSort(e.target.value); }} className="h-10 px-3 rounded-md border border-input bg-card text-sm md:col-span-4">
                    {SORT_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
            </div>

            <div className="border border-border rounded-md bg-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-secondary/40 border-b border-border">
                        <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                            <th className="px-4 py-3">Name</th>
                            <th className="px-4 py-3">Email</th>
                            <th className="px-4 py-3">Role</th>
                            <th className="px-4 py-3">Status</th>
                            <th className="px-4 py-3">Permissions</th>
                            <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-muted-foreground">No users.</td></tr>}
                        {items.map((u) => (
                            <tr key={u.id} data-testid={USERS.row} className={`border-b border-border last:border-0 hover:brightness-[0.99] transition-colors ${getStatusToneClasses(u.active === false ? "inactive" : "active", { group: "status", variant: "row" })}`}>
                                <td className="px-4 py-3 font-medium"><HighlightText text={u.name} query={q} /></td>
                                <td className="px-4 py-3 text-muted-foreground"><HighlightText text={u.email} query={q} /></td>
                                <td className="px-4 py-3 text-xs uppercase tracking-wider">{roleSummary(u.role)}</td>
                                <td className="px-4 py-3">
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wider border ${getStatusToneClasses(u.active === false ? "inactive" : "active", { group: "status", variant: "badge" })}`}>
                                        {u.active === false ? <ShieldOff className="h-3 w-3" /> : <Shield className="h-3 w-3" />}
                                        {getStatusLabel(u.active === false ? "inactive" : "active")}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-xs text-muted-foreground">
                                    {Object.entries(u.permissions || {}).filter(([, p]) => p?.view).map(([k]) => k).slice(0, 4).join(", ") || "—"}
                                </td>
                                <td className="px-4 py-3 text-right">
                                    <div className="inline-flex items-center gap-1">
                                        <button onClick={() => openEdit(u)} className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-secondary"><Edit3 className="h-3.5 w-3.5" /></button>
                                        <button onClick={() => resetPassword(u)} className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-secondary"><KeyRound className="h-3.5 w-3.5" /></button>
                                        <button onClick={() => remove(u)} className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-destructive/10 text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <PaginationBar total={total} page={page} limit={10} onPageChange={setPage} />

            {formOpen && (
                <UserModal
                    form={form}
                    setForm={setForm}
                    editing={editing}
                    onClose={() => { setFormOpen(false); setEditing(null); setForm(emptyForm()); }}
                    onSave={save}
                />
            )}
        </div>
    );
}

function UserModal({ form, setForm, editing, onClose, onSave }) {
    const togglePermission = (moduleKey, permKey) => {
        setForm((prev) => ({
            ...prev,
            permissions: {
                ...prev.permissions,
                [moduleKey]: {
                    ...(prev.permissions[moduleKey] || {}),
                    [permKey]: !prev.permissions[moduleKey]?.[permKey],
                },
            },
        }));
    };

    return (
        <div className="fixed inset-0 z-50 bg-background/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <form onSubmit={onSave} onClick={(e) => e.stopPropagation()} className="w-full max-w-5xl bg-card border border-border rounded-md shadow-xl overflow-hidden">
                <div className="p-5 border-b border-border flex items-center justify-between">
                    <div>
                        <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">{editing?.id ? "Edit User" : "Create User"}</div>
                        <h3 className="font-heading text-xl font-semibold mt-1">{form.name || "New user"}</h3>
                    </div>
                    <button type="button" onClick={onClose} className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-secondary"><X className="h-4 w-4" /></button>
                </div>
                <div className="p-5 max-h-[80vh] overflow-y-auto space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <label className="space-y-1.5">
                            <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Name</span>
                            <input required className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                        </label>
                        <label className="space-y-1.5">
                            <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Email</span>
                            <input required type="email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                        </label>
                        {!editing?.id && (
                            <label className="space-y-1.5">
                                <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Password</span>
                                <input required type="password" className={inputCls} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                            </label>
                        )}
                        <label className="space-y-1.5">
                            <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Role</span>
                            <select className={inputCls} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                                {["super_admin", "admin", "sales", "project_manager", "developer", "accounts", "custom"].map((role) => (
                                    <option key={role} value={role}>{role.replaceAll("_", " ")}</option>
                                ))}
                            </select>
                        </label>
                        <label className="space-y-1.5">
                            <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Custom Role Label</span>
                            <input className={inputCls} value={form.custom_role_name} onChange={(e) => setForm({ ...form, custom_role_name: e.target.value })} />
                        </label>
                        <label className="space-y-1.5">
                            <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Status</span>
                            <select className={inputCls} value={String(form.active)} onChange={(e) => setForm({ ...form, active: e.target.value === "true" })}>
                                <option value="true">Active</option>
                                <option value="false">Inactive</option>
                            </select>
                        </label>
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <h4 className="font-semibold">Module Permissions</h4>
                            <p className="text-xs text-muted-foreground">Toggle view/create/edit/delete/export for each module.</p>
                        </div>
                        <div className="border border-border rounded-md overflow-hidden">
                            <div className="grid grid-cols-6 gap-0 bg-secondary/30 text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">
                                <div className="px-3 py-2">Module</div>
                                {ACTIONS.map((a) => <div key={a} className="px-3 py-2 text-center">{a}</div>)}
                            </div>
                            {MODULES.map((m) => (
                                <div key={m.key} className="grid grid-cols-6 items-center border-t border-border">
                                    <div className="px-3 py-2 text-sm">{m.label}</div>
                                    {ACTIONS.map((a) => (
                                        <div key={a} className="px-3 py-2 text-center">
                                            <input
                                                type="checkbox"
                                                checked={!!form.permissions?.[m.key]?.[a]}
                                                onChange={() => togglePermission(m.key, a)}
                                            />
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="p-4 border-t border-border flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="h-10 px-4 rounded-md border border-border hover:bg-secondary text-sm">Cancel</button>
                    <button className="h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90">{editing?.id ? "Save Changes" : "Create User"}</button>
                </div>
            </form>
        </div>
    );
}
